import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { useDeviceType } from '../../hooks/useDeviceType.ts';

import PageError from '../../pages/Page404/PageError';

import {
    selectIsBtnLeftPanelEnabled,
    selectIsBtnMinimapEnabled,
    selectIsBtnPopupNewsEnabled,

    selectIsBtnExportImageEnabled,
    selectIsContentChanged,
} from '../../redux/diagram/diagram.selectors';

import { selectIsDiagramNotFound, selectIsQuickShareLinkInvalid, selectDiagramDetails } from '../../redux/main/main.selectors';

import {
    mainCanvasStarted,
    clearDiagramErrors,
    fetchDiagramMain,
    loadNewProject,
    loadQuickShareProject,
    loadSampleProjectDetails,
    handleQuickShareError,
} from '../../redux/main/main.actions';
import {
    canvasStarted,
    enableModalSignInRemind,
    loadSampleProjectData,
    loadQuickShareProjectData,
    // clearDiagramDetails,
} from '../../redux/diagram/diagram.actions';

import LoaderBar from '../../components/Loaders/LoaderBar/LoaderBar';

import DraggableTable from '../../components/DraggableElements/DraggableTable/DraggableTable';

import ControlsLeft from '../../components/Controls/ControlsLeft/ControlsLeft';
import ControlsBottom from '../../components/Controls/ControlsBottom/ControlsBottom';
import ControlsTopRight from '../../components/Controls/ControlsTopRight/ControlsTopRight';
import ControlsTopLeft from '../../components/Controls/ControlsTopLeft/ControlsTopLeft';


import ModalFeedback from '../../components/Modals/ModalFeedback/ModalFeedback';
import ModalFeedbackThanks from '../../components/Modals/ModalFeedbackThanks/ModalFeedbackThanks.jsx';

import ModalKeyboardShortcuts from '../../components/Modals/ModalKeyboardShortcuts/ModalKeyboardShortcuts';

import Minimap from '../../components/Minimap/Minimap';
import Canvas from '../../components/Canvas/Canvas';

import './Diagram.scss';
import Notify from '../../components/Notify/Notify';


import ModalDiagramDetails from '../../components/Modals/ModalDiagramDetails/ModalDiagramDetails';
import ModalDiagramCreateNew from '../../components/Modals/ModalDiagramCreateNew/ModalDiagramCreateNew';

import ModalSignInReminder from '../../components/Modals/ModalSignInReminder/ModalSignInReminder.jsx';
import ModalSignInRestriction from '../../components/Modals/ModalSignInRestriction/ModalSignInRestriction.jsx';

import ModalDiagramQuickShare from '../../components/Modals/ModalDiagramQuickShare/ModalDiagramQuickShare.jsx';
import ModalDiagramExportSQL from '../../components/Modals/ModalDiagramExportSQL/ModalDiagramExportSQL.jsx';

import ModalLanguages from '../../components/Modals/ModalLanguages/ModalLanguages';

import { decode } from '../../utils/quick-share/quick-share-decode';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const Diagram = ({ isNew = false, isQuickShare = false, quickShareId = '' }) => {
    const { diagramId } = useParams();
    const history = useHistory();
    const query = useQuery();

    const { isDesktop } = useDeviceType(); // mobile/tablet => false

    const isBtnLeftPanelEnabled = useSelector(selectIsBtnLeftPanelEnabled);
    const isBtnMinimapEnabled = useSelector(selectIsBtnMinimapEnabled);
    const isBtnPopupNewsEnabled = useSelector(selectIsBtnPopupNewsEnabled);

    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);

    const isDiagramNotFound = useSelector(selectIsDiagramNotFound);
    const isQuickShareLinkInvalid = useSelector(selectIsQuickShareLinkInvalid);

    const diagramDetails = useSelector(selectDiagramDetails);

    const loadingTimerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const isContentChanged = useSelector(selectIsContentChanged);

    useEffect(() => {
        if (isNew) {
            dispatch(loadNewProject());
        } else if (isQuickShare) {
            const q = query.get('q');
            if (q || quickShareId) {
                let [result, isError] = decode(q, quickShareId);
                if (isError) {
                    dispatch(handleQuickShareError());
                } else {
                    let [diagramDetails, elements, lines, areas] = result;
                    dispatch(loadQuickShareProject(diagramDetails));
                    dispatch(loadQuickShareProjectData(diagramDetails, elements, lines, areas));
                }
            } else {
                setTimeout(() => { history.push(`/`); }, 300);
            }
        } else {
            dispatch(loadSampleProjectDetails());
            dispatch(loadSampleProjectData());
            // dispatch(fetchDiagramMain(diagramId || 'default'));
        }

        dispatch(canvasStarted());
        dispatch(mainCanvasStarted());

        return () => {
            dispatch(clearDiagramErrors());
            // dispatch(clearDiagramDetails());
        };

    }, [diagramId, dispatch]);

    useEffect(() => {
        // set is page loading
        setIsLoading(true);

        // set timeout on page loading
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => {
            setIsLoading(false);
        }, 450);
        return () => { };
    }, []);

    // useEffect(() => {
    //     if (isLoading || !diagramDetails) {
    //         document.title = 'Loading diagram...';
    //     } else {
    //         document.title = `${diagramDetails.title || ''} - Playground DB Design`;
    //     }
    // }, [isLoading, diagramDetails]);

    useEffect(() => {
        if (diagramDetails) {
            const storedValue = sessionStorage.getItem('show-notify');
            if (storedValue !== 'true') {
                setTimeout(() => {
                    dispatch(enableModalSignInRemind());
                }, 1000);
            }
        }
    }, [diagramDetails]);


    // Подія beforeunload — системне попередження при закритті/перезавантаженні сторінки
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isContentChanged) {
                e.preventDefault();
                e.returnValue = ''; // Потрібно для активації діалогу
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Очищення при розмонтуванні компонента
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isContentChanged]);



    const canvasRef = useRef(null);
    const minimapRef = useRef(null);

    if (isDiagramNotFound) return <PageError type="playground" />;
    if (isQuickShareLinkInvalid) return <PageError type="quick-share" />;

    if (!isNew && !diagramDetails) return null;

    return (
        <div className="Diagram">
            <div className="panel-panel">
                <div className="panel-board">
                    <DraggableTable />
                    <ControlsTopLeft />
                    {isDesktop ? <ControlsTopRight /> : ''}
                    {true ? <ControlsLeft /> : ''}
                    <ControlsBottom canvasRef={canvasRef} minimapRef={minimapRef} />

                    <ModalKeyboardShortcuts />
                    <ModalLanguages />

                    <ModalFeedback />
                    <ModalFeedbackThanks />

                    <ModalDiagramDetails />
                    <ModalDiagramCreateNew />

                    {/* <ModalSignInReminder /> */}
                    <ModalSignInRestriction />

                    <ModalDiagramQuickShare />
                    <ModalDiagramExportSQL />

                    <Minimap ref={minimapRef} isOpen={isBtnMinimapEnabled && !isBtnExportImageEnabled} />

                    {/* <NotifyMessage /> */}
                    {/* <NotifyButtonAI /> */}
                    <Notify viewType='canvas' />

                    {Math.random() === 12 && <LoaderBar isLoading={isLoading} />}
                    <Canvas
                        isLoading={isLoading}
                        ref={canvasRef}
                        minimapRef={minimapRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default Diagram;
